import { useEffect, useState } from "react";
import { getSettings, updateSettings, resetAllData } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Separator } from "../components/ui/separator";
import { 
  Settings as SettingsIcon, 
  Building2, 
  CreditCard,
  Save,
  AlertTriangle,
  Trash2,
  Lock,
  Shield,
  GripVertical,
  ArrowUp,
  ArrowDown,
  Eye,
  EyeOff,
  Menu,
  RotateCcw,
  Plus,
  X,
  Truck,
  Mail,
  Upload,
  FlaskConical,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TABS = [
  { id: "general", label: "General", icon: SettingsIcon },
  { id: "email", label: "Email & Dispatch", icon: Mail },
  { id: "integrations", label: "Integrations", icon: Shield },
  { id: "navigation", label: "Navigation", icon: Menu },
  { id: "security", label: "Security", icon: Lock },
  { id: "danger", label: "Danger Zone", icon: AlertTriangle },
];

export default function Settings() {
  const { user, token, isAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState("general");
  const [settings, setSettings] = useState({
    invoice_prefix: "JIT",
    last_invoice_number: 100,
    financial_year: "25-26",
    gst_rate: 18.0,
    company_name: "VARDHMAN MARKETING",
    company_gst: "27AKRPS3339K1ZE",
    company_address: "H-18, 9TH FLOOR\nEVEREST BUILDING, TARDEO\nMUMBAI - 400034, INDIA",
    company_phone: "+919820512480",
    company_email: "raj@vardhmanmarketing.com",
    bank_name: "BANK OF BARODA",
    bank_branch: "TARDEO BRANCH, MUMBAI",
    account_number: "124602 00000 864",
    swift_code: "BARBOTARDEO"
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  
  const ALL_MODULES = [
    { id: "dashboard", label: "Dashboard" },
    { id: "po", label: "PO Processing" },
    { id: "inventory", label: "Inventory" },
    { id: "packaging", label: "Packaging" },
    { id: "display-kits", label: "Display Kits" },
    { id: "inventory-planning", label: "Inventory Planning" },
    { id: "rto", label: "RTO" },
    { id: "repairs", label: "Repairs" },
    { id: "invoices", label: "Invoice History" },
    { id: "email", label: "Email Dispatch" },
    { id: "analytics", label: "Revenue Analytics" },
    { id: "sales-analytics", label: "Sales Analytics" },
    { id: "settings", label: "Settings" },
    { id: "shipping", label: "Shipping Automation" },
    { id: "user-management", label: "User Management" },
    { id: "dispatch-cupboards", label: "Dispatch Cupboards" },
  ];
  const [menuOrder, setMenuOrder] = useState(ALL_MODULES.map(m => m.id));
  const [menuHidden, setMenuHidden] = useState([]);
  const [savingMenu, setSavingMenu] = useState(false);
  
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ currentPassword: "", newPassword: "", confirmPassword: "" });
  const [changingPassword, setChangingPassword] = useState(false);

  const [hsnRates, setHsnRates] = useState([]);
  const [newHsn, setNewHsn] = useState('');
  const [newRate, setNewRate] = useState('');

  const [engageToken, setEngageToken] = useState('');
  const [engageStatus, setEngageStatus] = useState(null);
  const [engageLoading, setEngageLoading] = useState(false);
  const [engageSaving, setEngageSaving] = useState(false);

  const [couriers, setCouriers] = useState([]);
  const [defaultCourier, setDefaultCourier] = useState('');
  const [hoEmail, setHoEmail] = useState('');
  const [testEmail, setTestEmail] = useState('');
  const [newCourierName, setNewCourierName] = useState('');
  const [newCourierUrl, setNewCourierUrl] = useState('');
  const [courierSaving, setCourierSaving] = useState(false);

  const [storeContacts, setStoreContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [editEmail, setEditEmail] = useState('');
  const [newContactName, setNewContactName] = useState('');
  const [newContactEmail, setNewContactEmail] = useState('');
  const [contactSearch, setContactSearch] = useState('');

  useEffect(() => {
    loadSettings();
    loadMenuSettings();
    loadEngageStatus();
    loadCourierSettings();
    loadStoreContacts();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await getSettings();
      setSettings(response.data);
      const rates = response.data.hsn_tax_rates || {};
      setHsnRates(Object.entries(rates).map(([hsn, rate]) => ({ hsn, rate: parseFloat(rate) })));
    } catch (error) {
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const loadMenuSettings = async () => {
    try {
      const res = await axios.get(`${API}/menu/settings`);
      if (res.data?.module_order) setMenuOrder(res.data.module_order);
      if (res.data?.hidden_modules) setMenuHidden(res.data.hidden_modules);
    } catch (error) { console.error("Failed to load menu settings"); }
  };

  const loadEngageStatus = async () => {
    setEngageLoading(true);
    try {
      const res = await axios.get(`${API}/engage/token-status`);
      setEngageStatus(res.data);
    } catch { }
    setEngageLoading(false);
  };

  const saveEngageToken = async () => {
    if (!engageToken.trim()) { toast.error("Please paste your Shiprocket token"); return; }
    setEngageSaving(true);
    try {
      await axios.post(`${API}/engage/token`, { token: engageToken.trim() });
      toast.success("Engage token saved & validated!");
      setEngageToken('');
      loadEngageStatus();
    } catch (err) { toast.error(err.response?.data?.detail || "Token validation failed"); }
    setEngageSaving(false);
  };

  const loadCourierSettings = async () => {
    try {
      const res = await axios.get(`${API}/courier-settings`);
      setCouriers(res.data.couriers || []);
      setDefaultCourier(res.data.default_courier || '');
      setHoEmail(res.data.ho_email || '');
      setTestEmail(res.data.test_email || '');
    } catch { }
  };

  const saveCourierSettings = async (updatedCouriers, updatedDefault, updatedHoEmail, updatedTestEmail) => {
    setCourierSaving(true);
    try {
      await axios.put(`${API}/courier-settings`, {
        couriers: updatedCouriers ?? couriers,
        default_courier: updatedDefault ?? defaultCourier,
        ho_email: updatedHoEmail ?? hoEmail,
        test_email: updatedTestEmail ?? testEmail,
      });
      toast.success("Settings saved");
    } catch (err) { toast.error("Failed to save settings"); }
    setCourierSaving(false);
  };

  const addCourier = () => {
    if (!newCourierName.trim()) return;
    const updated = [...couriers, { name: newCourierName.trim(), tracking_url_template: newCourierUrl.trim() }];
    setCouriers(updated);
    const def = couriers.length === 0 ? newCourierName.trim() : defaultCourier;
    setDefaultCourier(def);
    saveCourierSettings(updated, def, hoEmail, testEmail);
    setNewCourierName('');
    setNewCourierUrl('');
  };

  const removeCourier = (idx) => {
    const removed = couriers[idx].name;
    const updated = couriers.filter((_, i) => i !== idx);
    const def = defaultCourier === removed ? (updated[0]?.name || '') : defaultCourier;
    setCouriers(updated);
    setDefaultCourier(def);
    saveCourierSettings(updated, def, hoEmail, testEmail);
  };

  const setDefault = (name) => {
    setDefaultCourier(name);
    saveCourierSettings(couriers, name, hoEmail, testEmail);
  };

  const loadStoreContacts = async () => {
    setContactsLoading(true);
    try {
      const res = await axios.get(`${API}/store-contacts`);
      setStoreContacts(res.data);
    } catch { }
    setContactsLoading(false);
  };

  const updateContact = async (storeName) => {
    try {
      await axios.put(`${API}/store-contacts/${encodeURIComponent(storeName)}`, { email: editEmail });
      toast.success("Email updated");
      setEditingContact(null);
      loadStoreContacts();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to update"); }
  };

  const addContact = async () => {
    if (!newContactName.trim() || !newContactEmail.trim()) return;
    try {
      await axios.post(`${API}/store-contacts/add`, { store_name: newContactName.trim(), email: newContactEmail.trim() });
      toast.success("Contact added");
      setNewContactName('');
      setNewContactEmail('');
      loadStoreContacts();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to add"); }
  };

  const deleteContact = async (storeName) => {
    try {
      await axios.delete(`${API}/store-contacts/${encodeURIComponent(storeName)}`);
      toast.success("Contact removed");
      loadStoreContacts();
    } catch { }
  };

  const uploadContacts = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await axios.post(`${API}/store-contacts/upload`, fd);
      toast.success(`Imported ${res.data.imported} contacts`);
      loadStoreContacts();
    } catch (err) { toast.error("Upload failed"); }
    e.target.value = '';
  };

  const saveMenuSettings = async () => {
    setSavingMenu(true);
    try {
      await axios.put(`${API}/menu/settings`, { groups: [], module_order: menuOrder, hidden_modules: menuHidden });
      toast.success("Menu layout saved! Refresh the page to see changes in the sidebar.");
    } catch (error) { toast.error("Failed to save menu settings"); }
    finally { setSavingMenu(false); }
  };

  const resetMenuSettings = async () => {
    try {
      const res = await axios.post(`${API}/menu/reset`);
      if (res.data?.module_order) setMenuOrder(res.data.module_order);
      setMenuHidden([]);
      toast.success("Menu reset to defaults");
    } catch (error) { toast.error("Failed to reset menu"); }
  };

  const moveMenuItem = (index, direction) => {
    const newOrder = [...menuOrder];
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= newOrder.length) return;
    [newOrder[index], newOrder[targetIndex]] = [newOrder[targetIndex], newOrder[index]];
    setMenuOrder(newOrder);
  };

  const toggleMenuVisibility = (moduleId) => {
    if (moduleId === "dashboard" || moduleId === "settings") return;
    setMenuHidden(prev => prev.includes(moduleId) ? prev.filter(m => m !== moduleId) : [...prev, moduleId]);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const hsnDict = {};
      hsnRates.forEach(({ hsn, rate }) => { hsnDict[hsn] = rate; });
      await updateSettings({ ...settings, hsn_tax_rates: hsnDict });
      toast.success("Settings saved successfully");
    } catch (error) { toast.error("Failed to save settings"); }
    finally { setSaving(false); }
  };

  const addHsnRate = () => {
    if (!newHsn.trim() || !newRate) { toast.error("Please enter both HSN code and tax rate"); return; }
    if (hsnRates.find(r => r.hsn === newHsn.trim())) { toast.error("HSN code already exists"); return; }
    setHsnRates(prev => [...prev, { hsn: newHsn.trim(), rate: parseFloat(newRate) }]);
    setNewHsn(''); setNewRate('');
  };

  const removeHsnRate = (hsn) => { setHsnRates(prev => prev.filter(r => r.hsn !== hsn)); };
  const updateHsnRate = (hsn, newRateValue) => { setHsnRates(prev => prev.map(r => r.hsn === hsn ? { ...r, rate: parseFloat(newRateValue) } : r)); };

  const handleReset = async () => {
    const confirmMsg = "This will DELETE ALL data:\n- All Purchase Orders\n- All Invoices\n- All Inventory\n\nType 'RESET' to confirm:";
    const userInput = window.prompt(confirmMsg);
    if (userInput !== 'RESET') { if (userInput !== null) toast.error("Reset cancelled - confirmation text did not match"); return; }
    setResetting(true);
    try {
      const response = await resetAllData();
      toast.success(`Data reset complete: ${response.data.deleted.purchase_orders} POs, ${response.data.deleted.invoices} invoices, ${response.data.deleted.inventory} inventory items deleted`);
      await loadSettings();
    } catch (error) { toast.error("Failed to reset data"); }
    finally { setResetting(false); }
  };

  const handleChange = (field, value) => { setSettings(prev => ({ ...prev, [field]: value })); };

  const handlePasswordChange = async () => {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) { toast.error("New passwords don't match"); return; }
    if (passwordForm.newPassword.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setChangingPassword(true);
    try {
      await axios.put(`${API}/auth/change-password?token=${token}&current_password=${encodeURIComponent(passwordForm.currentPassword)}&new_password=${encodeURIComponent(passwordForm.newPassword)}`);
      toast.success("Password changed successfully");
      setShowPasswordChange(false);
      setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to change password"); }
    finally { setChangingPassword(false); }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;
  }

  return (
    <div className="animate-fadeIn max-w-5xl" data-testid="settings-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Settings</h2>
          <p className="text-sm text-slate-500 mt-0.5">Manage your application configuration</p>
        </div>
        {activeTab === "general" && (
          <Button onClick={handleSave} disabled={saving} size="sm" data-testid="save-settings-btn">
            {saving ? <div className="spinner w-4 h-4 mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Save Changes
          </Button>
        )}
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-1 border-b border-slate-200 mb-5 overflow-x-auto" data-testid="settings-tabs">
        {TABS.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`settings-tab-${tab.id}`}
              className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium whitespace-nowrap border-b-2 transition-colors ${
                activeTab === tab.id
                  ? "border-slate-800 text-slate-800"
                  : "border-transparent text-slate-400 hover:text-slate-600"
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* TAB: General */}
      {activeTab === "general" && (
        <div className="space-y-5">
          {/* Invoice Settings */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-blue-100 rounded-md flex items-center justify-center"><SettingsIcon className="w-4 h-4 text-blue-600" /></div>
                <div><CardTitle className="text-base">Invoice Settings</CardTitle><CardDescription className="text-xs">Configure invoice numbering and tax rates</CardDescription></div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="invoice_prefix" className="text-xs">Invoice Prefix</Label>
                  <Input id="invoice_prefix" value={settings.invoice_prefix} onChange={e => handleChange('invoice_prefix', e.target.value)} placeholder="JIT" data-testid="invoice-prefix-input" />
                  <p className="text-[11px] text-slate-400">e.g., JIT, INV, VM</p>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="last_invoice_number" className="text-xs">Last Invoice Number</Label>
                  <Input id="last_invoice_number" type="number" value={settings.last_invoice_number} onChange={e => handleChange('last_invoice_number', parseInt(e.target.value))} data-testid="last-invoice-number-input" />
                  <p className="text-[11px] text-slate-400">Next: {settings.invoice_prefix}{settings.last_invoice_number + 1}-{settings.financial_year}</p>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="financial_year" className="text-xs">Financial Year</Label>
                  <Input id="financial_year" value={settings.financial_year} onChange={e => handleChange('financial_year', e.target.value)} placeholder="25-26" />
                  <p className="text-[11px] text-slate-400">Format: YY-YY</p>
                </div>
              </div>
              <Separator />
              <div className="space-y-1.5 max-w-xs">
                <Label htmlFor="gst_rate" className="text-xs">Default GST Rate (%)</Label>
                <Input id="gst_rate" type="number" step="0.1" value={settings.gst_rate} onChange={e => handleChange('gst_rate', parseFloat(e.target.value))} />
                <p className="text-[11px] text-slate-400">Fallback rate when HSN-specific rate is not set</p>
              </div>
              <Separator />
              <div className="space-y-3">
                <div>
                  <Label className="text-xs font-semibold">HSN Tax Rates</Label>
                  <p className="text-[11px] text-slate-400 mt-0.5">Set specific GST rates per HSN code</p>
                </div>
                {hsnRates.length > 0 && (
                  <div className="space-y-1.5">
                    {hsnRates.map(({ hsn, rate }) => (
                      <div key={hsn} className="flex items-center gap-3 p-2 bg-slate-50 rounded-lg border text-sm" data-testid={`hsn-rate-${hsn}`}>
                        <code className="text-xs font-mono bg-white px-2 py-0.5 rounded border flex-1">{hsn}</code>
                        <div className="flex items-center gap-1">
                          <Input type="number" step="0.1" value={rate} onChange={e => updateHsnRate(hsn, e.target.value)} className="w-20 h-7 text-xs" data-testid={`hsn-rate-input-${hsn}`} />
                          <span className="text-[11px] text-slate-400">%</span>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => removeHsnRate(hsn)} className="h-6 w-6 p-0 text-red-500 hover:text-red-700" data-testid={`hsn-remove-${hsn}`}><X className="w-3 h-3" /></Button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Input placeholder="HSN Code" value={newHsn} onChange={e => setNewHsn(e.target.value)} className="flex-1 h-8 text-sm" data-testid="hsn-new-code-input" />
                  <Input type="number" step="0.1" placeholder="Rate %" value={newRate} onChange={e => setNewRate(e.target.value)} className="w-24 h-8 text-sm" data-testid="hsn-new-rate-input" />
                  <Button variant="outline" size="sm" onClick={addHsnRate} className="h-8" data-testid="hsn-add-btn"><Plus className="w-3 h-3 mr-1" />Add</Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Company Details */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-emerald-100 rounded-md flex items-center justify-center"><Building2 className="w-4 h-4 text-emerald-600" /></div>
                <div><CardTitle className="text-base">Company Details</CardTitle><CardDescription className="text-xs">Your company information on invoices</CardDescription></div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5"><Label className="text-xs">Company Name</Label><Input value={settings.company_name} onChange={e => handleChange('company_name', e.target.value)} /></div>
                <div className="space-y-1.5"><Label className="text-xs">GSTIN</Label><Input value={settings.company_gst} onChange={e => handleChange('company_gst', e.target.value)} /></div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Address</Label>
                <textarea className="w-full min-h-[70px] px-3 py-2 border border-slate-200 rounded-md text-sm" value={settings.company_address} onChange={e => handleChange('company_address', e.target.value)} />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5"><Label className="text-xs">Phone</Label><Input value={settings.company_phone} onChange={e => handleChange('company_phone', e.target.value)} /></div>
                <div className="space-y-1.5"><Label className="text-xs">Email</Label><Input type="email" value={settings.company_email} onChange={e => handleChange('company_email', e.target.value)} /></div>
              </div>
            </CardContent>
          </Card>

          {/* Bank Details */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-amber-100 rounded-md flex items-center justify-center"><CreditCard className="w-4 h-4 text-amber-600" /></div>
                <div><CardTitle className="text-base">Bank Details</CardTitle><CardDescription className="text-xs">Payment information shown on invoices</CardDescription></div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5"><Label className="text-xs">Bank Name</Label><Input value={settings.bank_name} onChange={e => handleChange('bank_name', e.target.value)} /></div>
                <div className="space-y-1.5"><Label className="text-xs">Branch</Label><Input value={settings.bank_branch} onChange={e => handleChange('bank_branch', e.target.value)} /></div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5"><Label className="text-xs">Account Number</Label><Input value={settings.account_number} onChange={e => handleChange('account_number', e.target.value)} /></div>
                <div className="space-y-1.5"><Label className="text-xs">SWIFT/IFSC Code</Label><Input value={settings.swift_code} onChange={e => handleChange('swift_code', e.target.value)} /></div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* TAB: Email & Dispatch */}
      {activeTab === "email" && (
        <div className="space-y-5">
          {/* Email Addresses */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-violet-100 rounded-md flex items-center justify-center"><Mail className="w-4 h-4 text-violet-600" /></div>
                <div><CardTitle className="text-base">Email Addresses</CardTitle><CardDescription className="text-xs">Configure HQ and test email recipients</CardDescription></div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-xs">Head Office Email</Label>
                  <p className="text-[11px] text-slate-400">Receives dispatch summary with all POs/PIs</p>
                  <div className="flex gap-2">
                    <Input data-testid="ho-email-input" value={hoEmail} onChange={e => setHoEmail(e.target.value)} placeholder="e.g. headoffice@justintime.in" className="h-8 text-sm" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs flex items-center gap-1.5"><FlaskConical className="w-3 h-3" /> Test Email</Label>
                  <p className="text-[11px] text-slate-400">Safe address for testing — never sends to stores or HQ</p>
                  <div className="flex gap-2">
                    <Input data-testid="test-email-input" value={testEmail} onChange={e => setTestEmail(e.target.value)} placeholder="e.g. test@yourcompany.com" className="h-8 text-sm" />
                  </div>
                </div>
              </div>
              <Button
                onClick={() => saveCourierSettings(couriers, defaultCourier, hoEmail, testEmail)}
                disabled={courierSaving}
                size="sm"
                data-testid="save-email-settings"
              >
                <Save className="w-3.5 h-3.5 mr-1.5" />
                {courierSaving ? "Saving..." : "Save Email Settings"}
              </Button>
            </CardContent>
          </Card>

          {/* Courier Settings */}
          <Card data-testid="courier-settings-card">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-blue-100 rounded-md flex items-center justify-center"><Truck className="w-4 h-4 text-blue-600" /></div>
                <div><CardTitle className="text-base">Couriers</CardTitle><CardDescription className="text-xs">Manage couriers and tracking URL templates</CardDescription></div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {couriers.length > 0 && (
                <div className="space-y-1.5">
                  {couriers.map((c, i) => (
                    <div key={i} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg text-sm">
                      <div className="flex-1">
                        <span className="font-medium text-slate-800">{c.name}</span>
                        {defaultCourier === c.name && <span className="ml-2 text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full font-medium">DEFAULT</span>}
                        {c.tracking_url_template && <p className="text-[11px] text-slate-400 font-mono truncate mt-0.5">{c.tracking_url_template}</p>}
                      </div>
                      {defaultCourier !== c.name && <button onClick={() => setDefault(c.name)} className="text-[11px] text-blue-600 hover:text-blue-800" data-testid={`set-default-${i}`}>Set default</button>}
                      <button onClick={() => removeCourier(i)} className="text-slate-400 hover:text-red-500" data-testid={`remove-courier-${i}`}><X className="w-3.5 h-3.5" /></button>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex gap-2 items-end">
                <div className="flex-1 space-y-1">
                  <Label className="text-xs">Courier Name</Label>
                  <Input data-testid="new-courier-name" value={newCourierName} onChange={e => setNewCourierName(e.target.value)} placeholder="e.g. FedEx" className="h-8 text-sm" />
                </div>
                <div className="flex-[2] space-y-1">
                  <Label className="text-xs">Tracking URL Template</Label>
                  <Input data-testid="new-courier-url" value={newCourierUrl} onChange={e => setNewCourierUrl(e.target.value)} placeholder="e.g. https://fedex.com/track?trknbr={awb}" className="h-8 text-sm font-mono" />
                </div>
                <Button onClick={addCourier} disabled={!newCourierName.trim()} size="sm" className="h-8" data-testid="add-courier-btn"><Plus className="w-3.5 h-3.5 mr-1" /> Add</Button>
              </div>
              <p className="text-[11px] text-slate-400">Use <code className="bg-slate-100 px-1 rounded">{'{awb}'}</code> as placeholder in the tracking URL.</p>
            </CardContent>
          </Card>

          {/* Store Email Directory */}
          <Card data-testid="store-contacts-card">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-amber-100 rounded-md flex items-center justify-center"><Mail className="w-4 h-4 text-amber-600" /></div>
                  <div><CardTitle className="text-base">Store Email Directory</CardTitle><CardDescription className="text-xs">{storeContacts.length} stores registered</CardDescription></div>
                </div>
                <label className="cursor-pointer">
                  <input type="file" accept=".xlsx,.xls" className="hidden" onChange={uploadContacts} data-testid="upload-contacts-input" />
                  <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 rounded-md text-xs font-medium text-slate-700 transition-colors"><Upload className="w-3.5 h-3.5" /> Upload Excel</span>
                </label>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <Input data-testid="contact-search" value={contactSearch} onChange={e => setContactSearch(e.target.value)} placeholder="Search stores..." className="h-8 text-sm" />
              <div className="flex gap-2 items-end">
                <div className="flex-1 space-y-1">
                  <Label className="text-xs">Store Name</Label>
                  <Input data-testid="new-contact-name" value={newContactName} onChange={e => setNewContactName(e.target.value)} placeholder="Store name" className="h-8 text-sm" />
                </div>
                <div className="flex-1 space-y-1">
                  <Label className="text-xs">Email</Label>
                  <Input data-testid="new-contact-email" value={newContactEmail} onChange={e => setNewContactEmail(e.target.value)} placeholder="email@domain.com" className="h-8 text-sm" />
                </div>
                <Button onClick={addContact} disabled={!newContactName.trim() || !newContactEmail.trim()} size="sm" className="h-8" data-testid="add-contact-btn"><Plus className="w-3.5 h-3.5 mr-1" /> Add</Button>
              </div>
              <div className="max-h-[300px] overflow-y-auto border rounded-lg">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left text-slate-500">Store</th>
                      <th className="px-3 py-2 text-left text-slate-500">Email</th>
                      <th className="px-3 py-2 w-20"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {storeContacts.filter(c => !contactSearch || c.store_name.toLowerCase().includes(contactSearch.toLowerCase())).map(c => (
                      <tr key={c.store_name} className="hover:bg-slate-50">
                        <td className="px-3 py-1.5 font-medium text-slate-700">{c.store_name}</td>
                        <td className="px-3 py-1.5">
                          {editingContact === c.store_name ? (
                            <div className="flex gap-1">
                              <Input value={editEmail} onChange={e => setEditEmail(e.target.value)} className="h-6 text-xs" />
                              <button onClick={() => updateContact(c.store_name)} className="text-emerald-600 text-[11px] font-medium">Save</button>
                              <button onClick={() => setEditingContact(null)} className="text-slate-400 text-[11px]">Cancel</button>
                            </div>
                          ) : <span className="text-slate-500">{c.email}</span>}
                        </td>
                        <td className="px-3 py-1.5 text-right">
                          {editingContact !== c.store_name && (
                            <div className="flex gap-1 justify-end">
                              <button onClick={() => { setEditingContact(c.store_name); setEditEmail(c.email); }} className="text-blue-500 text-[11px] hover:text-blue-700">Edit</button>
                              <button onClick={() => deleteContact(c.store_name)} className="text-red-400 text-[11px] hover:text-red-600">Del</button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* TAB: Integrations */}
      {activeTab === "integrations" && (
        <div className="space-y-5">
          <Card className="border-blue-200" data-testid="engage-token-card">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-blue-100 rounded-md flex items-center justify-center"><Shield className="w-4 h-4 text-blue-600" /></div>
                <div><CardTitle className="text-base">Shiprocket Engage Token</CardTitle><CardDescription className="text-xs">Required for accurate RTO% in Shipping Automation</CardDescription></div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {engageLoading ? (
                <p className="text-sm text-slate-400">Checking token status...</p>
              ) : engageStatus?.has_token ? (
                <div className={`flex items-center gap-2 p-3 rounded-lg text-sm ${engageStatus.is_valid ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-red-50 text-red-700 border border-red-200"}`} data-testid="engage-token-status">
                  {engageStatus.is_valid ? (
                    <><div className="w-2 h-2 bg-emerald-500 rounded-full"></div>Token active — saved {engageStatus.saved_at ? new Date(engageStatus.saved_at).toLocaleDateString() : ""}</>
                  ) : (
                    <><AlertTriangle className="w-4 h-4" />Token expired — please refresh using the steps below</>
                  )}
                </div>
              ) : (
                <div className="flex items-center gap-2 p-3 bg-amber-50 text-amber-700 border border-amber-200 rounded-lg text-sm" data-testid="engage-token-missing">
                  <AlertTriangle className="w-4 h-4" />No token set — RTO% data will be inaccurate
                </div>
              )}
              <div className="bg-slate-50 rounded-lg p-4 text-sm text-slate-700 space-y-3">
                <p className="font-semibold">How to connect (one-time, takes 30 seconds):</p>
                <div className="space-y-2">
                  <p><strong>Step 1:</strong> Open <a href="https://app.shiprocket.in" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">app.shiprocket.in</a> in a new tab and log in normally</p>
                  <p><strong>Step 2:</strong> Once logged in, copy this entire line and paste it into the <strong>address bar</strong>, then press Enter:</p>
                  <div className="relative">
                    <code className="block bg-slate-800 text-green-400 p-3 rounded text-xs overflow-x-auto whitespace-nowrap select-all" data-testid="bookmarklet-code">
                      {`javascript:void(fetch("${BACKEND_URL}/api/engage/token",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({token:localStorage.getItem("satellizer_token")})}).then(r=>r.json()).then(d=>d.success?alert("Token saved successfully! You can close this tab."):alert("Error: "+d.detail)).catch(()=>alert("Failed to connect. Make sure you are logged into Shiprocket.")))`}
                    </code>
                    <button className="absolute top-1 right-1 bg-slate-700 hover:bg-slate-600 text-white text-xs px-2 py-1 rounded" data-testid="copy-bookmarklet" onClick={() => {
                      const code = `javascript:void(fetch("${BACKEND_URL}/api/engage/token",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({token:localStorage.getItem("satellizer_token")})}).then(r=>r.json()).then(d=>d.success?alert("Token saved successfully! You can close this tab."):alert("Error: "+(d.detail||"Unknown error"))).catch(()=>alert("Failed to connect")))`;
                      navigator.clipboard.writeText(code);
                      toast.success("Copied! Now paste it in the Shiprocket tab's address bar");
                    }}>Copy</button>
                  </div>
                  <p className="text-xs text-slate-500">Note: Some browsers remove the "javascript:" part when pasting.</p>
                  <p><strong>Step 3:</strong> You should see an alert saying "Token saved successfully!" — come back here and refresh</p>
                </div>
                <p className="text-xs text-slate-400">The token lasts ~10 days. When it expires, repeat these steps.</p>
              </div>
              <details className="text-sm">
                <summary className="cursor-pointer text-slate-500 hover:text-slate-700">Alternative: Paste token manually</summary>
                <div className="mt-2 space-y-2 pl-2">
                  <p className="text-xs text-slate-500">Open DevTools (F12) on the Shiprocket page, go to Application &rarr; Local Storage &rarr; find <code className="bg-slate-200 px-1 rounded">satellizer_token</code> and copy its value.</p>
                  <div className="flex gap-2">
                    <Input data-testid="engage-token-input" value={engageToken} onChange={e => setEngageToken(e.target.value)} placeholder="Paste token here..." className="font-mono text-xs" type="password" />
                    <Button onClick={saveEngageToken} disabled={engageSaving || !engageToken.trim()} data-testid="save-engage-token" size="sm">{engageSaving ? "Validating..." : "Save"}</Button>
                  </div>
                </div>
              </details>
            </CardContent>
          </Card>
        </div>
      )}

      {/* TAB: Navigation */}
      {activeTab === "navigation" && isAdmin() && (
        <div className="space-y-5">
          <Card className="border-indigo-200">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-indigo-100 rounded-md flex items-center justify-center"><Menu className="w-4 h-4 text-indigo-600" /></div>
                <div><CardTitle className="text-base">Navigation Menu</CardTitle><CardDescription className="text-xs">Reorder and toggle visibility of sidebar tabs</CardDescription></div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-1.5 mb-4">
                {menuOrder.map((moduleId, index) => {
                  const mod = ALL_MODULES.find(m => m.id === moduleId);
                  if (!mod) return null;
                  const isHidden = menuHidden.includes(moduleId);
                  const isProtected = moduleId === "dashboard" || moduleId === "settings";
                  return (
                    <div key={moduleId} className={`flex items-center gap-3 p-2.5 rounded-lg border transition-colors ${isHidden ? 'bg-slate-50 border-slate-200 opacity-60' : 'bg-white border-slate-200'}`} data-testid={`menu-item-${moduleId}`}>
                      <GripVertical className="w-4 h-4 text-slate-400 flex-shrink-0" />
                      <span className={`flex-1 text-sm font-medium ${isHidden ? 'text-slate-400 line-through' : 'text-slate-700'}`}>{mod.label}</span>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" onClick={() => moveMenuItem(index, -1)} disabled={index === 0} className="h-7 w-7 p-0" data-testid={`menu-up-${moduleId}`}><ArrowUp className="w-3.5 h-3.5" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => moveMenuItem(index, 1)} disabled={index === menuOrder.length - 1} className="h-7 w-7 p-0" data-testid={`menu-down-${moduleId}`}><ArrowDown className="w-3.5 h-3.5" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => toggleMenuVisibility(moduleId)} disabled={isProtected} className="h-7 w-7 p-0" title={isProtected ? "Cannot hide" : (isHidden ? "Show" : "Hide")} data-testid={`menu-toggle-${moduleId}`}>
                          {isHidden ? <EyeOff className="w-3.5 h-3.5 text-slate-400" /> : <Eye className="w-3.5 h-3.5 text-emerald-500" />}
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex items-center gap-2 justify-end">
                <Button variant="outline" size="sm" onClick={resetMenuSettings} data-testid="menu-reset-btn"><RotateCcw className="w-3.5 h-3.5 mr-1.5" />Reset to Default</Button>
                <Button size="sm" onClick={saveMenuSettings} disabled={savingMenu} data-testid="menu-save-btn">{savingMenu ? "Saving..." : "Save Menu Layout"}</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* TAB: Security */}
      {activeTab === "security" && (
        <div className="space-y-5">
          <Card className="border-purple-200">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-purple-100 rounded-md flex items-center justify-center"><Lock className="w-4 h-4 text-purple-600" /></div>
                <div><CardTitle className="text-base">Account Security</CardTitle><CardDescription className="text-xs">Manage your account password</CardDescription></div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between p-4 bg-purple-50 rounded-lg border border-purple-200">
                <div>
                  <p className="font-medium text-purple-800 text-sm">Change Password</p>
                  <p className="text-xs text-purple-600">Update your login password</p>
                </div>
                <Button variant="outline" className="border-purple-300 text-purple-700 hover:bg-purple-100" onClick={() => setShowPasswordChange(!showPasswordChange)} data-testid="change-password-btn" size="sm"><Lock className="w-3.5 h-3.5 mr-1.5" />Change Password</Button>
              </div>
              {showPasswordChange && (
                <div className="mt-4 p-4 bg-white rounded-lg border space-y-4">
                  <div className="space-y-1.5"><Label className="text-xs">Current Password</Label><Input type="password" value={passwordForm.currentPassword} onChange={e => setPasswordForm({...passwordForm, currentPassword: e.target.value})} placeholder="Enter current password" data-testid="current-password-input" /></div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1.5"><Label className="text-xs">New Password</Label><Input type="password" value={passwordForm.newPassword} onChange={e => setPasswordForm({...passwordForm, newPassword: e.target.value})} placeholder="Min 6 characters" data-testid="new-password-input" /></div>
                    <div className="space-y-1.5"><Label className="text-xs">Confirm New Password</Label><Input type="password" value={passwordForm.confirmPassword} onChange={e => setPasswordForm({...passwordForm, confirmPassword: e.target.value})} placeholder="Confirm new password" data-testid="confirm-password-input" /></div>
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" size="sm" onClick={() => setShowPasswordChange(false)}>Cancel</Button>
                    <Button onClick={handlePasswordChange} disabled={changingPassword} data-testid="save-password-btn" size="sm">{changingPassword ? "Changing..." : "Update Password"}</Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* TAB: Danger Zone */}
      {activeTab === "danger" && (
        <div className="space-y-5">
          <Card className="border-red-200">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-red-100 rounded-md flex items-center justify-center"><AlertTriangle className="w-4 h-4 text-red-600" /></div>
                <div><CardTitle className="text-base text-red-700">Danger Zone</CardTitle><CardDescription className="text-xs">Irreversible actions - use with caution</CardDescription></div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg border border-red-200">
                <div>
                  <p className="font-medium text-red-800 text-sm">Reset All Data</p>
                  <p className="text-xs text-red-600">Delete all POs, invoices, and inventory. Cannot be undone.</p>
                </div>
                <Button variant="destructive" onClick={handleReset} disabled={resetting} data-testid="reset-all-btn" size="sm">
                  {resetting ? <><div className="spinner w-4 h-4 mr-2" />Resetting...</> : <><Trash2 className="w-3.5 h-3.5 mr-1.5" />Reset All Data</>}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
